import {CompositeFilterCtrl} from '../../filter/model/CompositeFilterCtrl';
import {DateRangeFilterCtrl} from '../../filter/model/DateRangeFilterCtrl';
import {FieldValueFilterCtrl} from '../../filter/model/FieldValueFilterCtrl';
import {QueryStringFilterCtrl} from '../../filter/model/QueryStringFilterCtrl';
import {BoolOperator, BoolOperatorEnum} from '../../filter/model/utils/BoolOperatorEnum';
import {toArray} from '../../utils/collectionUtils';
import {BoolFilter} from '../../utils/query/filter/BoolFilter';
import {DateRangeFilter} from '../../utils/query/filter/DateRangeFilter';
import {MissingFilter} from '../../utils/query/filter/MissingFilter';
import {QueryStringFilter} from '../../utils/query/filter/QueryStringFilter';
import {TermFilter} from '../../utils/query/filter/TermFilter';
import {TermsFilter} from '../../utils/query/filter/TermsFilter';
import {NoneFieldValue} from '../model/field/NoneFieldValue';
import {services} from './services';
import {ApiFilter} from "../../utils/query/filter/ApiFilter";
import {AbstractFilterCtrl} from "../../filter/model/AbstractFilterCtrl";
import _ from "lodash";
import {FieldValue} from "../model/field/FieldValue";
import {DateRange} from "../model/DateRange";
import {dateFormat} from "../../utils/dateUtils";
import {NotEmptyVerbatimsFilterCtrl} from "../../filter/model/NotEmptyVerbatimsFilterCtrl";
import {VerbatimNotEmptyFilter} from "../../utils/query/filter/VerbatimNotEmptyFilter";
import {FieldQueryStringFilterCtrl} from "../../filter/model/FieldQueryStringFilterCtrl";
import {NestedFilter} from "../../utils/query/filter/NestedFilter";

export class FilterConverterService {

    wrapNested(apiFilter: ApiFilter, verbatimNestedBoolFilter: BoolFilter, boolFilterOp: BoolOperator): ApiFilter {
        if (!apiFilter) return apiFilter;
        const field = apiFilter['field'];
        if (field && ('' + field).startsWith("verbatims.")) {
            if (!verbatimNestedBoolFilter) return new NestedFilter("verbatims", apiFilter);

            if (boolFilterOp === BoolOperatorEnum.MUST) {
                verbatimNestedBoolFilter.must.push(apiFilter);
            } else if (boolFilterOp === BoolOperatorEnum.SHOULD) {
                verbatimNestedBoolFilter.should.push(apiFilter);
            }
            return null;
        }
        return apiFilter;
    }

    convertToApiFilter(filter: AbstractFilterCtrl<unknown>, excludedFilters: [string], verbatimsNestedFilter: BoolFilter, verbatimsNestedFilterBoolOp: BoolOperator): ApiFilter {
        if (_.isNil(filter)) {
            return null;
        }
        if (excludedFilters && excludedFilters.indexOf(filter.id) >= 0) {
            return null;
        }
        const value = filter.getValue();

        if (_.isNil(value)) {
            return null;
        }

        if (filter instanceof CompositeFilterCtrl) {
            const nextLevelVerbatimsNestedBoolFilter = new BoolFilter(null, null, null);
            const nextLevelVerbatimsNestedFilter = new NestedFilter('verbatims', nextLevelVerbatimsNestedBoolFilter);

            let convertedFilters = filter.filters.map(f => this.convertToApiFilter(f, excludedFilters, nextLevelVerbatimsNestedBoolFilter, filter.boolOperator)).filter(a => !_.isNil(a));

            if ((filter.boolOperator === BoolOperatorEnum.MUST && nextLevelVerbatimsNestedBoolFilter.must.length > 0)
                || (filter.boolOperator === BoolOperatorEnum.SHOULD && nextLevelVerbatimsNestedBoolFilter.should.length > 0)) {
                convertedFilters = convertedFilters.concat(nextLevelVerbatimsNestedFilter);
            }

            if (convertedFilters.length === 0) {
                return null;
            }

            if (convertedFilters.length === 1) {
                return convertedFilters[0];
            }

            const boolFilter = new BoolFilter(null, null, null);
            if (filter.boolOperator === BoolOperatorEnum.MUST) {
                boolFilter.must = convertedFilters;
            } else if (filter.boolOperator === BoolOperatorEnum.SHOULD) {
                boolFilter.should = convertedFilters;
            }
            return boolFilter;
        }

        if (filter instanceof FieldValueFilterCtrl) {
            const values: Array<FieldValue> = toArray(value as OneOrMany<FieldValue>);

            let hasNoneFieldValue = false;
            const terms = [];

            values.forEach(v => {
                if (v instanceof NoneFieldValue) {
                    hasNoneFieldValue = true;
                } else if (!_.isNil(v)) {
                    terms.push(v.fullName);
                }
            });

            let termsFilter;
            if (terms.length === 0) {
                termsFilter = null;
            } else if (terms.length === 1) {
                termsFilter = new TermFilter(filter.field.name, terms[0]);
            } else {
                termsFilter = new TermsFilter(filter.field.name, terms);
            }

            return this.wrapNested(BoolFilter.should(termsFilter, hasNoneFieldValue ? new MissingFilter(filter.field.name) : null), verbatimsNestedFilter, verbatimsNestedFilterBoolOp);
        }

        if (filter instanceof DateRangeFilterCtrl) {
            const dateRange: DateRange = value as DateRange;

            return this.wrapNested(new DateRangeFilter(filter.field.name, services.getTimeZoneService().getTimeZone(),
                dateRange.start.toFormat(dateFormat), dateRange.end.toFormat(dateFormat)), verbatimsNestedFilter, verbatimsNestedFilterBoolOp);
        }

        if (filter instanceof QueryStringFilterCtrl) {
            if (_.isEmpty(value)) {
                return null;
            }
            return new QueryStringFilter(value as string, null, true);
        }

        if (filter instanceof FieldQueryStringFilterCtrl) {
            if (_.isEmpty(value)) {
                return null;
            }
            return this.wrapNested(new TermFilter(filter.field.name, value as string),  verbatimsNestedFilter, verbatimsNestedFilterBoolOp);
        }

        if (filter instanceof NotEmptyVerbatimsFilterCtrl) {
            return !value ? null : new VerbatimNotEmptyFilter();
        }

        return null;
    }
}

services.registerService('filterConverterService', new FilterConverterService());


